import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import Layout from "@/components/Layout";
import Modal from "@/components/Modal";
import { euro, productLabel } from "@/lib/format";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";

const emptyV = { product_id: "", sku: "", color: "", size: "", qty: 0, min: 2, location: "" };
const emptyM = { variant_id: "", type: "Carico", qty: 1, note: "" };

export default function Magazzino() {
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [vModal, setVModal] = useState(false);
  const [mModal, setMModal] = useState(false);
  const [vForm, setVForm] = useState(emptyV);
  const [mForm, setMForm] = useState(emptyM);
  const [vEditing, setVEditing] = useState(null);

  const load = async () => {
    const [p, v] = await Promise.all([api.get("/products"), api.get("/variants")]);
    setProducts(p.data); setVariants(v.data);
  };
  useEffect(() => { load(); }, []);

  const openNewV = () => { setVForm({ ...emptyV, product_id: products[0]?.id || "" }); setVEditing(null); setVModal(true); };
  const openEditV = (v) => { setVForm({ ...emptyV, ...v }); setVEditing(v.id); setVModal(true); };
  const saveV = async () => {
    if (!vForm.product_id) return toast.error("Seleziona un prodotto");
    if (!vForm.sku.trim()) return toast.error("Inserisci lo SKU");
    try {
      const body = { ...vForm, qty: +vForm.qty || 0, min: +vForm.min || 0 };
      if (vEditing) await api.put(`/variants/${vEditing}`, body);
      else await api.post("/variants", body);
      toast.success("Variante salvata"); setVModal(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const delV = async (id) => {
    if (!window.confirm("Eliminare la variante?")) return;
    await api.delete(`/variants/${id}`); toast.success("Eliminata"); load();
  };
  const openMovement = (variantId = "") => { setMForm({ ...emptyM, variant_id: variantId }); setMModal(true); };
  const saveM = async () => {
    if (!mForm.variant_id) return toast.error("Seleziona una variante");
    try {
      await api.post("/movements", { ...mForm, qty: Math.abs(+mForm.qty || 0) });
      toast.success("Movimento registrato"); setMModal(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const rows = variants.filter((v) => {
    const match = (v.sku + " " + productLabel(products, v.product_id) + " " + (v.color || "") + " " + (v.size || "")).toLowerCase().includes(q.toLowerCase());
    const low = Number(v.qty) <= Number(v.min || 0);
    return match && (!filter || (filter === "low" ? low : !low));
  });

  return (
    <Layout topbarActions={
      <button className="btn accent" onClick={openNewV} data-testid={IDS.newVariantBtn}>
        <i className="fa-solid fa-plus" /> Nuova variante
      </button>
    }>
      <div className="section-title">
        <div><h2>Magazzino</h2><p>Disponibilità aggiornata per modello, colore e taglia.</p></div>
        <button className="btn primary" onClick={openNewV}><i className="fa-solid fa-plus" /> Nuova variante</button>
      </div>
      <div className="panel">
        <div className="panel-head">
          <div className="toolbar">
            <input placeholder="Cerca prodotto, SKU o colore..." value={q} onChange={(e) => setQ(e.target.value)} />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">Tutte le scorte</option>
              <option value="low">Sotto scorta</option>
              <option value="available">Disponibili</option>
            </select>
          </div>
          <button className="btn" onClick={() => openMovement("")} data-testid={IDS.newMovementBtn}>
            <i className="fa-solid fa-right-left" /> Registra movimento
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>SKU</th><th>Prodotto</th><th>Colore</th><th>Taglia</th><th>Disponibilità</th><th>Scorta min.</th><th>Valore</th><th></th></tr></thead>
            <tbody data-testid="stock-table-body">
              {rows.length ? rows.map((v) => {
                const p = products.find((x) => x.id === v.product_id);
                const low = Number(v.qty) <= Number(v.min || 0);
                return (
                  <tr key={v.id}>
                    <td><b>{v.sku}</b></td>
                    <td>{productLabel(products, v.product_id)}</td>
                    <td>{v.color || "—"}</td>
                    <td><span className="pill">{v.size || "—"}</span></td>
                    <td className={low ? "stock-low" : ""}>{Number(v.qty) || 0}</td>
                    <td>{Number(v.min) || 0}</td>
                    <td>{euro((p?.cost || 0) * (v.qty || 0))}</td>
                    <td style={{ display: "flex", gap: 6 }}>
                      <button className="btn small" onClick={() => openMovement(v.id)}><i className="fa-solid fa-right-left" /></button>
                      <button className="btn small" onClick={() => openEditV(v)}><i className="fa-solid fa-pen" /></button>
                      <button className="btn small danger" onClick={() => delV(v.id)}><i className="fa-solid fa-trash" /></button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={8} className="empty">Nessuna variante trovata.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={vModal} title={vEditing ? "Modifica variante" : "Nuova variante di magazzino"} onClose={() => setVModal(false)} onSave={saveV}>
        {products.length ? (
          <div className="form-grid">
            <div className="field full"><label>Prodotto *</label>
              <select value={vForm.product_id} onChange={(e) => setVForm({ ...vForm, product_id: e.target.value })}>
                <option value="">— seleziona —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </select>
            </div>
            <div className="field"><label>SKU *</label><input value={vForm.sku} onChange={(e) => setVForm({ ...vForm, sku: e.target.value })} placeholder="es. BODY-001-NER-10" data-testid="v-sku" /></div>
            <div className="field"><label>Colore</label><input value={vForm.color} onChange={(e) => setVForm({ ...vForm, color: e.target.value })} /></div>
            <div className="field"><label>Taglia</label><input value={vForm.size} onChange={(e) => setVForm({ ...vForm, size: e.target.value })} /></div>
            <div className="field"><label>Quantità {vEditing ? "" : "iniziale"}</label><input type="number" value={vForm.qty} onChange={(e) => setVForm({ ...vForm, qty: e.target.value })} disabled={!!vEditing} /></div>
            <div className="field"><label>Scorta minima</label><input type="number" value={vForm.min} onChange={(e) => setVForm({ ...vForm, min: e.target.value })} /></div>
            <div className="field full"><label>Ubicazione</label><input value={vForm.location} onChange={(e) => setVForm({ ...vForm, location: e.target.value })} placeholder="es. Scaffale A2" /></div>
          </div>
        ) : <div className="empty">Prima crea almeno un prodotto in Anagrafiche.</div>}
      </Modal>

      <Modal open={mModal} title="Movimento di magazzino" onClose={() => setMModal(false)} onSave={saveM}>
        {variants.length ? (
          <div className="form-grid">
            <div className="field full"><label>Variante *</label>
              <select value={mForm.variant_id} onChange={(e) => setMForm({ ...mForm, variant_id: e.target.value })}>
                <option value="">— seleziona —</option>
                {variants.map((v) => <option key={v.id} value={v.id}>{productLabel(products, v.product_id)} · {v.color || "N/D"} · {v.size || "N/D"} (SKU {v.sku})</option>)}
              </select>
            </div>
            <div className="field"><label>Tipo</label>
              <select value={mForm.type} onChange={(e) => setMForm({ ...mForm, type: e.target.value })}>
                <option>Carico</option><option>Scarico</option>
                <option>Rettifica positiva</option><option>Rettifica negativa</option>
              </select>
            </div>
            <div className="field"><label>Quantità *</label><input type="number" min="1" value={mForm.qty} onChange={(e) => setMForm({ ...mForm, qty: e.target.value })} /></div>
            <div className="field full"><label>Nota</label><textarea rows="3" value={mForm.note} onChange={(e) => setMForm({ ...mForm, note: e.target.value })} /></div>
          </div>
        ) : <div className="empty">Prima crea almeno una variante.</div>}
      </Modal>
    </Layout>
  );
}
